import type { FieldPolicy, TypePolicies } from '@apollo/client';

const typesWithScenarios = [
	'Stakeholder', 'Risk', 'Stake',
	'Indicator', 'FishboneItem', 'FishboneBranch'
];

// Read-defaults : les écritures PARTIELLES au cache (optimistic
// responses, inserts realtime) ne portent pas les champs résolus
// par field-resolvers (listes de liens/impacts) ni les ids
// optionnels (catégories). Sans défaut, Apollo considère l'objet
// incomplet -> cache miss -> refetch getProject intempestif (ce qui
// cassait notamment les tutoriaux). Un `read` avec défaut rend le
// cache tolérant ; les vraies valeurs arrivent au fetch complet.
const emptyList: FieldPolicy = { read: (existing: unknown) => existing ?? [] };
const nullable: FieldPolicy = { read: (existing: unknown) => existing ?? null };

// `scenarios` : remplacé en bloc (pas de merge) et vide par défaut — un
// objet inséré par le realtime n'a pas encore de surcharges de scénario
// (le stream omet `scenarios: {}`), il ne doit pas rendre getProject
// incomplet pour autant.
const scenariosPolicy: FieldPolicy = { merge: false, read: (existing: unknown) => existing ?? [] };

// `projectId` : sélectionné par getProject, absent des réponses create*.
// Sans défaut, chaque création rend getProject incomplet -> Apollo le relance
// sur le réseau, et ce refetch (parti avant updateXxxScenario) écrase la
// surcharge de scénario d'une création en mode scénario : l'élément créé
// disparaissait de la vue scénario jusqu'au rechargement.
const commonFields: Record<string, FieldPolicy> = { projectId: nullable };

const defaultFields: Record<string, Record<string, FieldPolicy>> = {
	Risk: {
		impactedStakes: emptyList,
		impactedIndicators: emptyList,
		openingLinks: emptyList,
		fishboneLinks: emptyList,
		taggings: emptyList,
		riskCategoryId: nullable,
		comment: nullable,
		matchingOpportunityId: nullable
	},
	Stake: {
		fishboneLinks: emptyList,
		impactedIndicators: emptyList,
		taggings: emptyList
	},
	Stakeholder: {
		links: emptyList,
		impactedStakes: emptyList,
		impactedIndicators: emptyList,
		impactedRisks: emptyList,
		fishboneLinks: emptyList,
		taggings: emptyList
	},
	Indicator: {
		fishboneLinks: emptyList,
		indicatorCategoryId: nullable
	},
	FishboneBranch: {
		items: emptyList,
		riskLinks: emptyList,
		stakeLinks: emptyList,
		indicatorLinks: emptyList,
		stakeholderLinks: emptyList
	},
	FishboneItem: {
		riskLinks: emptyList,
		stakeLinks: emptyList,
		indicatorLinks: emptyList,
		stakeholderLinks: emptyList
	}
};

export const buildTypePolicies = (): TypePolicies =>
	Object.fromEntries(
		typesWithScenarios.map(type => [
			type,
			{
				fields: {
					scenarios: scenariosPolicy,
					...commonFields,
					...(defaultFields[type] || {})
				}
			}
		])
	);
